import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import LoginIcon from '@mui/icons-material/Login';
import RefreshIcon from '@mui/icons-material/Refresh';
import CircularProgress from '@mui/material/CircularProgress';
import React from 'react';
import { useSessionActions } from '../hooks/use-session-actions';
import { ALLIANCE_THRESHOLDS } from './alliance/alliance-tokens';
import { useAppBanners } from './alliance/app-banners';
import { BarButton, useIsMobileLayout } from './alliance/bar-controls';

/**
 * The App Bar's Sign in button for anonymous visitors (Events is public and
 * has no Sign In page; this goes straight to the Cognito Hosted UI).
 *
 * While the redirect starts it shows a spinner and "Opening sign-in…" and
 * ignores further clicks. If starting it throws, or the page is still here
 * after ALLIANCE_THRESHOLDS.signInFailureMs, the button resets and an error
 * banner under the bar says "We couldn't start sign-in", with Try again.
 */
export function SignInBarButton() {
  // State
  const [pending, setPending] = React.useState(false);
  // Ref
  const timerRef = React.useRef<number>();
  // Read by the banner's Try again too, which holds an older handler
  const pendingRef = React.useRef(false);
  // The failure banner's id: one per failure, because a dismissed id stays
  // hidden for the session and a later failure must still show
  const bannerIdRef = React.useRef<string | null>(null);
  // Hooks
  const isMobile = useIsMobileLayout();
  const { push, remove } = useAppBanners();
  const { signInAgain } = useSessionActions();

  React.useEffect(() => {
    // Back from the Hosted UI via the browser's back/forward cache: the page
    // is restored as it was, mid-redirect, so start over
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        window.clearTimeout(timerRef.current);
        pendingRef.current = false;
        setPending(false);
      }
    };
    window.addEventListener('pageshow', handlePageShow);
    return () => {
      window.removeEventListener('pageshow', handlePageShow);
      window.clearTimeout(timerRef.current);
    };
  }, []);

  const handleSignIn = async () => {
    if (pendingRef.current) {
      return;
    }

    // Shows the banner and resets the button
    const fail = () => {
      window.clearTimeout(timerRef.current);
      pendingRef.current = false;
      setPending(false);
      bannerIdRef.current = `sign-in-failed-${Date.now()}`;
      push({
        id: bannerIdRef.current,
        tone: 'error',
        icon: <ErrorOutlineIcon />,
        message: 'We couldn’t start sign-in. Please try again.',
        actions: [
          {
            label: 'Try again',
            icon: <RefreshIcon sx={{ fontSize: 18 }} />,
            onClick: handleSignIn,
            outlined: true,
          },
        ],
        dismissible: true,
      });
    };

    if (bannerIdRef.current) {
      remove(bannerIdRef.current);
    }
    pendingRef.current = true;
    setPending(true);
    window.clearTimeout(timerRef.current);
    // The redirect normally unloads the page; if it hasn't, it failed
    timerRef.current = window.setTimeout(
      fail,
      ALLIANCE_THRESHOLDS.signInFailureMs,
    );
    try {
      await signInAgain();
    } catch (error) {
      fail();
    }
  };

  return (
    <BarButton
      onClick={handleSignIn}
      aria-disabled={pending}
      aria-busy={pending}
      sx={{
        gap: 1,
        p: '0 16px 0 12px',
        fontSize: 14,
        fontWeight: 500,
        whiteSpace: 'nowrap',
        cursor: pending ? 'progress' : 'pointer',
      }}
    >
      {pending ? (
        <CircularProgress size={18} thickness={5} sx={{ color: '#ffffff' }} />
      ) : (
        <LoginIcon sx={{ fontSize: 20 }} />
      )}
      {/* Phones keep the short label so the bar doesn't overflow */}
      {pending && !isMobile ? 'Opening sign-in…' : 'Sign in'}
    </BarButton>
  );
}
